import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { authApi, authApiV2 } from '@/api/endpoints/auth';
import { getDevAccessToken, getDevUserSession, isDevAuth } from '@/lib/dev-auth';
import { initAnalytics } from '@/lib/analytics';
import { clearMonitoringUser, setMonitoringUser } from '@/lib/monitoring/identity';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { runImpersonationHandoff } from '@/features/auth/lib/impersonation-handoff';
import {
  parkImpersonationTarget,
  readImpersonationTarget,
} from '@/features/auth/lib/impersonation-target';
import { requiresPasswordReset, SET_PASSWORD_ROUTE } from '@/features/auth/lib/password-reset';
import { attachSessionWakeRefresh } from '@/features/auth/lib/refresh-session-on-visible';
import { shouldRestoreSessionAfterLogout } from '@/features/auth/lib/explicit-logout';
import { performSignOut } from '@/features/auth/lib/sign-out';
import {
  isPublicAuthPathname,
  isPreLogGuestPathname,
  shouldSkipSupabaseHydration,
  shouldIgnoreAuthSignedOut,
} from '@/features/auth/lib/login-path';
import { DEFAULT_POST_AUTH_PATH, mfaVerifyRoute, normalizePostAuthPath } from '@/lib/default-landing';
import {
  dashboardAccessContextQueryKey,
} from '@/features/dashboard/hooks/useDashboardAccessContext';
import { dashboardApi } from '@/api/endpoints/dashboard';
import type { LoginRequest, LoginResponse, UserSession } from '@/types/auth';

/**
 * Access-context is keyed by userId. After impersonation handoff the store
 * holds the target — prefetch must use that id, not the SSI operator's.
 *
 * @param preHandoffUserId - Session user id before {@link runImpersonationHandoff}
 * @param postHandoffUserId - `useAuthStore.getState().user?.userId` after handoff
 */
export function accessContextUserIdForPrefetch(
  preHandoffUserId: number,
  postHandoffUserId: number | undefined,
): number {
  return postHandoffUserId && postHandoffUserId > 0 ? postHandoffUserId : preHandoffUserId;
}

/**
 * Drop every access-context row, then prefetch under `userId` so LandingRedirect
 * does not reuse an SSI-keyed cache after impersonation (FR-90).
 */
export async function prefetchDashboardAccessContext(
  queryClient: QueryClient,
  userId: number,
): Promise<void> {
  queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
  if (userId <= 0) return;
  await queryClient.fetchQuery({
    queryKey: dashboardAccessContextQueryKey(userId),
    queryFn: () => dashboardApi.getAccessContext(),
  });
}

/** Safe in-app path only — reject protocol-relative / absolute URLs. */
function safeReturnTo(path: string | undefined): string {
  return normalizePostAuthPath(path);
}

function toSession(res: LoginResponse): UserSession {
  const u = res.user;
  if (!u || !res.accessToken) {
    throw new Error('Login response missing session tokens');
  }
  return {
    userId: u.userId,
    temporaryUsersId: null,
    username: u.email,
    email: u.email,
    token: res.accessToken,
    role: u.role ?? '',
    roleId: 0,
    companyId: u.companyId ?? 0,
    companyName: u.companyName ?? '',
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
    isActiveDirectory: u.authProvider === 'entra',
    isSessionActivated: true,
    isApproved: true,
    active: true,
    authProvider: u.authProvider,
    mustChangePassword: u.mustChangePassword === true,
    mustCompleteName: u.mustCompleteName === true,
    suggestedFirstName: u.suggestedFirstName,
    suggestedLastName: u.suggestedLastName,
    mustAcceptLegal: u.mustAcceptLegal === true,
  };
}

/** Initialize auth state — if tokens are stored, fetch /auth/me; else fall through. */
export function useAuthInit() {
  const { setUser, setLoading, logout, accessToken, setCompanyFlags, setBuildings, setTokens } = useAuthStore();
  const isImpersonating = useAuthStore((s) => !!s.realUser);
  const devAuth = isDevAuth();

  useEffect(() => {
    if (devAuth && !isImpersonating) {
      const devUser = getDevUserSession();
      const devToken = getDevAccessToken();
      setUser(devUser);
      setTokens({ accessToken: devToken, refreshToken: 'dev-refresh' });
      initAnalytics({
        userId: devUser.userId,
        email: devUser.email,
        companyId: devUser.companyId,
      });
      setMonitoringUser(devUser);
    }
  }, [devAuth, setUser, isImpersonating, setTokens]);

  // Interim Supabase-auth bridge: Supabase owns the session (persisted +
  // auto-refreshed by supabase-js). We mirror its access token into the auth
  // store so the API client attaches it as the Bearer, then the /auth/me query
  // below hydrates the rich UserSession. The onAuthStateChange listener keeps
  // the store token fresh across silent refreshes and clears it on sign-out.
  useEffect(() => {
    if (devAuth || isImpersonating || !isSupabaseLoginEnabled()) return;
    const supabase = getSupabaseClient();
    void supabase.auth.getSession().then(async ({ data }) => {
      // Recovery / MFA / SSO-callback own hydration. Pre-log pages
      // (login/register/forgot-password) MUST hydrate so a live session
      // bounces into the app (DEF-432) — unless this tab just signed out
      // (FR-330 / legacy `/login?fromLogout=true`).
      if (shouldSkipSupabaseHydration()) {
        setLoading(false);
        return;
      }
      if (data.session) {
        if (!shouldRestoreSessionAfterLogout()) {
          await supabase.auth.signOut({ scope: 'local' });
          setLoading(false);
          return;
        }
        setTokens({
          accessToken: data.session.access_token,
          refreshToken: data.session.refresh_token,
        });
        return;
      }
      // Company Sign out (FR-330) must not silent-resume. SSI keeps the
      // pre-cut remember-me resume so SSI Login is unchanged.
      if (shouldRestoreSessionAfterLogout()) {
        try {
          const resumed = await authApiV2.resumeRememberMe();
          if (resumed?.accessToken && resumed.refreshToken) {
            setTokens({
              accessToken: resumed.accessToken,
              refreshToken: resumed.refreshToken,
            });
            await getSupabaseClient().auth.setSession({
              access_token: resumed.accessToken,
              refresh_token: resumed.refreshToken,
            });
            return;
          }
        } catch {
          // Dependency failure on resume — fall through to anonymous.
        }
      }
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        // SSO / MFA own hydration. Do not kill the incoming Microsoft
        // session — that was Cindy's FR-330 bounce (SSI Login twice).
        if (!shouldRestoreSessionAfterLogout() && !shouldSkipSupabaseHydration()) {
          void supabase.auth.signOut({ scope: 'local' });
          return;
        }
        setTokens({ accessToken: session.access_token, refreshToken: session.refresh_token });
      } else if (event === 'SIGNED_OUT') {
        const auth = useAuthStore.getState();
        if (
          shouldIgnoreAuthSignedOut({
            hasPendingTwoFactor: Boolean(auth.pendingTwoFactor),
            isAuthenticated: auth.isAuthenticated,
            hasAccessToken: Boolean(auth.accessToken),
          })
        ) {
          return;
        }
        clearMonitoringUser();
        logout();
      }
    });
    // DEF-418 residual — wake refresh so long-idle tabs (esp. SSI) do not die at JWT expiry.
    const detachWakeRefresh = attachSessionWakeRefresh(supabase);
    return () => {
      detachWakeRefresh();
      sub.subscription.unsubscribe();
    };
  }, [devAuth, isImpersonating, setTokens, setLoading, logout]);

  const hasToken = Boolean(accessToken);
  const onSsoCallback =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/v2/auth/sso/callback');
  const onPublicAuthPage =
    typeof window !== 'undefined' && isPublicAuthPathname();
  const onPreLogGuest =
    typeof window !== 'undefined' && isPreLogGuestPathname();

  const { isLoading, data: meData, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const session = await authApiV2.me();
      setUser(session);
      initAnalytics({
        userId: session.userId,
        email: session.email,
        companyId: session.companyId,
      });
      setMonitoringUser(session);
      if (session.companyFlags) setCompanyFlags(session.companyFlags);
      if (session.buildings) setBuildings(session.buildings);
      return session;
    },
    retry: false,
    enabled:
      !devAuth &&
      !isImpersonating &&
      hasToken &&
      !onSsoCallback &&
      (!onPublicAuthPage || onPreLogGuest),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!devAuth && !isImpersonating) {
      // Block the shell only on the *first* /auth/me hydration. Background
      // refetches after login used to flip isLoading back on and leave
      // ProtectedRoute on a blank spinner until a hard refresh.
      setLoading(hasToken && !meData ? isLoading : false);
    }
  }, [isLoading, meData, setLoading, devAuth, isImpersonating, hasToken]);

  useEffect(() => {
    if (error && !devAuth) {
      // SSO callback owns hydration (link → refresh → me). A background /me
      // failure there must not clear tokens mid-flight or the callback page
      // loses the session before it can surface a useful error.
      const onSsoCallback =
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/v2/auth/sso/callback');
      if (onSsoCallback) return;
      if (typeof window !== 'undefined' && isPreLogGuestPathname()) {
        // DEF-432 runs /auth/me on login when tokens exist. A stale JWT from a
        // failed MFA attempt must be cleared — otherwise /me 401s forever on the
        // login form and the user cannot sign in again.
        if (isAxiosError(error) && error.response?.status === 401) {
          logout();
          if (isSupabaseLoginEnabled()) {
            void getSupabaseClient().auth.signOut({ scope: 'local' });
          }
        }
        return;
      }
      if (typeof window !== 'undefined' && isPublicAuthPathname()) {
        return;
      }
      logout();
    }
  }, [error, logout, devAuth]);
}

/**
 * Outcome of a login attempt — discriminated so onSuccess can route MFA,
 * password-reset, and session hydration without re-checking flags.
 */
type LoginOutcome =
  | { kind: 'session'; res: LoginResponse; returnTo: string }
  | { kind: 'mfa'; res: LoginResponse; returnTo: string }
  | { kind: 'supabase-reset'; returnTo: string };

/**
 * Login mutation. Always posts to V3 `POST /api/auth/login` so lockout,
 * AC-LOG-004, and MFA gates run server-side. On success, bridges Supabase
 * tokens into the store (and mirrors them into supabase-js when enabled)
 * then hydrates `/auth/me`.
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, setTokens, setPendingTwoFactor } = useAuthStore();

  return useMutation({
    mutationFn: async (data: LoginRequest): Promise<LoginOutcome> => {
      const returnTo = safeReturnTo(data.returnTo);
      const impersonationTargetEmail = readImpersonationTarget();
      useAuthStore.getState().logout();
      if (impersonationTargetEmail) {
        parkImpersonationTarget(impersonationTargetEmail);
      }

      if (isSupabaseLoginEnabled()) {
        await getSupabaseClient().auth.signOut({ scope: 'local' });
      }

      const res = await authApiV2.login({
        email: data.email,
        password: data.password,
      });

      if (res.requiresMfa) {
        return {
          kind: 'mfa',
          res,
          returnTo,
        };
      }

      if (!res.accessToken || !res.refreshToken) {
        throw new Error('Login response missing session tokens');
      }

      setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });

      if (isSupabaseLoginEnabled()) {
        const { error } = await getSupabaseClient().auth.setSession({
          access_token: res.accessToken,
          refresh_token: res.refreshToken,
        });
        if (error) throw error;
        const { data: sessionData } = await getSupabaseClient().auth.getSession();
        if (sessionData.session && requiresPasswordReset(sessionData.session)) {
          return { kind: 'supabase-reset', returnTo };
        }
      }

      return { kind: 'session', res, returnTo };
    },
    onSuccess: async (outcome) => {
      if (outcome.kind === 'supabase-reset') {
        navigate(SET_PASSWORD_ROUTE);
        return;
      }
      if (outcome.kind === 'mfa') {
        const codeSent = Boolean(outcome.res.channel);
        const method =
          outcome.res.channel === 'sms'
            ? 'sms'
            : outcome.res.channel === 'email'
              ? 'email'
              : (outcome.res.availableChannels?.[0] ?? 'email');
        setPendingTwoFactor({
          email: outcome.res.user?.email ?? '',
          userId: outcome.res.user?.userId ?? outcome.res.userId ?? 0,
          method,
          phoneMasked: outcome.res.phoneMasked ?? '***-***-****',
          mfaChallengeToken: outcome.res.mfaChallengeToken,
          availableChannels: outcome.res.availableChannels,
          codeSent,
          devSecret: outcome.res.devSecret,
          returnTo:
            outcome.returnTo === DEFAULT_POST_AUTH_PATH ? undefined : outcome.returnTo,
        });
        navigate(mfaVerifyRoute(outcome.returnTo), { state: { returnTo: outcome.returnTo } });
        return;
      }

      const ssiUser = toSession(outcome.res);
      // Prefer /auth/me for roleId + flags when the token is live.
      let handedOff = false;
      try {
        const session = await authApiV2.me();
        setUser(session);
        setMonitoringUser(session);
        queryClient.setQueryData(['auth', 'me'], session);
        handedOff = await runImpersonationHandoff(session);
        // Handoff may swap the session to an impersonated user — refresh tags.
        const after = useAuthStore.getState().user;
        if (after) setMonitoringUser(after);
        // FR-88 — Partners is keyed by user/company but still 60s-stale; drop
        // the SSI see-all list on impersonation start so the next mount refetches.
        queryClient.invalidateQueries({ queryKey: ['teams', 'partners'] });
        // FR-90 — key access-context by the post-handoff user, not the SSI id.
        await prefetchDashboardAccessContext(
          queryClient,
          accessContextUserIdForPrefetch(session.userId, after?.userId),
        );
      } catch {
        setUser(ssiUser);
        setMonitoringUser(ssiUser);
        handedOff = await runImpersonationHandoff(ssiUser);
        const after = useAuthStore.getState().user;
        if (after) setMonitoringUser(after);
        queryClient.invalidateQueries({ queryKey: ['teams', 'partners'] });
        await prefetchDashboardAccessContext(
          queryClient,
          accessContextUserIdForPrefetch(ssiUser.userId, after?.userId),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      // Re-run LandingRedirect so the impersonated user's tier picks the page.
      navigate(handedOff ? DEFAULT_POST_AUTH_PATH : outcome.returnTo);
    },
  });
}

/** Legacy login (dev-stub /Account/login) — kept for back-compat tests. */
export function useLegacyLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, setPendingTwoFactor } = useAuthStore();

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (user) => {
      if (user.requiresTwoFactor) {
        const method =
          user.twoFactorMethod === 'sms' || user.twoFactorMethod === 'email'
            ? user.twoFactorMethod
            : 'email';
        setPendingTwoFactor({
          email: user.email,
          userId: user.userId,
          method,
          phoneMasked: user.twoFactorPhoneMasked ?? '***-***-****',
        });
        navigate('/mfa/verify');
      } else {
        setUser(user);
        queryClient.invalidateQueries({ queryKey: ['auth'] });
        navigate(DEFAULT_POST_AUTH_PATH);
      }
    },
  });
}

/** Logout mutation — revokes the stored refresh token then clears state. */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      // Clear Zendesk Messenger identity before TruSpace session ends so the
      // next account does not inherit the previous conversation (US-2).
      try {
        const { logoutZendeskUser } = await import('@/lib/zendesk');
        logoutZendeskUser();
      } catch {
        // Best-effort — Zendesk may not be loaded.
      }
      const impersonating = Boolean(useAuthStore.getState().realUser);
      await performSignOut({ exitImpersonation: impersonating });
    },
    onSuccess: () => {
      clearMonitoringUser();
      queryClient.clear();
      queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'partners'] });
      navigate('/login', { replace: true });
    },
    onError: () => {
      clearMonitoringUser();
      queryClient.clear();
      queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'partners'] });
      navigate('/login', { replace: true });
    },
  });
}
